<template>
  <span 
    ref="referenceRef"
    :class="['tooltip-wrapper', { 'tooltip-wrapper--inline': inline }]"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focusin="showTooltip"
    @focusout="hideTooltip"
  >
    <slot />
  </span>
  <Teleport to="body">
    <Transition name="tooltip">
      <div
        v-if="isVisible"
        ref="floatingRef"
        :style="floatingStyles"
        class="tooltip"
        role="tooltip"
      >
        <slot name="content">{{ content }}</slot>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onUnmounted, nextTick } from 'vue'
import { useFloating, autoUpdate, offset, shift, flip } from '@floating-ui/vue'

const props = defineProps({
  content: {
    type: String,
    default: ''
  },
  placement: {
    type: String,
    default: 'right'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  /**
   * L'enveloppe fait la taille de ce qu'elle enrobe, au lieu de prendre toute
   * la largeur disponible. `width: 100%` est utile autour d'un champ qui
   * remplit sa colonne, désastreux autour d'un bouton de 36 px : dans la grille
   * des formules, les deux boutons de palier se retrouvaient chacun à 239 px de
   * large, donc écartés de 243 px aux deux bouts de leur case.
   */
  inline: {
    type: Boolean,
    default: false
  }
})

const isVisible = ref(false)
const referenceRef = ref(null)
const floatingRef = ref(null)

/**
 * Élément de référence pour le positionnement. Le wrapper est la référence
 * naturelle ; mais quand son contenu est positionné en absolu (barres d'une
 * frise, jalons d'une timeline…), le wrapper n'a plus de boîte (hauteur ou
 * largeur nulle) et l'infobulle s'affichait au bord de la piste, loin du
 * curseur. Dans ce cas seulement, on s'ancre sur le premier enfant élément —
 * celui que l'utilisateur survole réellement. Élément virtuel au sens de
 * Floating UI (`contextElement` = le wrapper, pour `autoUpdate`).
 */
const anchorRef = computed(() => {
  const wrapper = referenceRef.value
  if (!wrapper) return null
  return {
    getBoundingClientRect() {
      const rect = wrapper.getBoundingClientRect()
      const child = wrapper.firstElementChild
      if (child && (rect.width === 0 || rect.height === 0)) return child.getBoundingClientRect()
      return rect
    },
    contextElement: wrapper,
  }
})

const { floatingStyles, update } = useFloating(anchorRef, floatingRef, {
  placement: props.placement,
  middleware: [
    offset(8),
    shift({ padding: 8 }),
    flip()
  ]
})

let cleanup = null

const showTooltip = async () => {
  if (props.disabled) return
  isVisible.value = true
  await nextTick()
  if (anchorRef.value && floatingRef.value) {
    cleanup = autoUpdate(anchorRef.value, floatingRef.value, update)
  }
}

const hideTooltip = () => {
  isVisible.value = false
  if (cleanup) {
    cleanup()
    cleanup = null
  }
}

watch(() => props.disabled, (newVal) => {
  if (newVal) {
    hideTooltip()
  }
})

watch(() => props.placement, () => {
  if (isVisible.value) {
    update()
  }
})

onUnmounted(() => {
  if (cleanup) {
    cleanup()
  }
})
</script>

<style scoped>
.tooltip-wrapper {
  display: flex;
  width: 100%;
}

/* Voir la prop `inline` : l'infobulle ne décide pas de la largeur de ce qu'elle
   enrobe. */
.tooltip-wrapper--inline {
  flex: 0 0 auto;
  width: auto;
}

.tooltip {
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  white-space: normal;
  max-width: 300px;
  word-wrap: break-word;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  z-index: var(--z-tooltip);
  pointer-events: none;
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity var(--transition-base);
}

.tooltip-enter-from {
  opacity: 0;
}

.tooltip-leave-to {
  opacity: 0;
}
</style>
